/** How long "Link copied" stays on the button before it says "Copy link" again. */
export const COPIED_FOR_MS = 2200;

/** Matches the zod schema on the API, which matches the rule in viewing-rooms.rules.ts. */
export const PASSWORD_MIN = 6;

/**
 * Works shown in a room's preview before it says "and N more".
 *
 * Enough to recognise the room at a glance and not so many that a room of three
 * hundred becomes the screen. A count alone does not tell a collector whether
 * they picked the right pieces; four or five thumbnails do.
 */
export const PREVIEW_LIMIT = 6;

/**
 * Most things one room may name, matching `workIds: z.array(...).max(500)` in
 * apps/api/src/viewing-rooms/viewing-rooms.schema.ts.
 *
 * Unreachable while the picker was a flat row of chips, and reachable the day it
 * became paged. Mirrored here so the limit is felt at the point of the decision
 * — a disabled row — rather than at submit, where the API answers "Some of those
 * details are not valid" and deliberately names nothing.
 */
export const MAX_CHOSEN = 500;

/** The three things a room can be built over. */
export const SOURCE_KINDS = [
  { key: "all", label: "Everything I hold" },
  { key: "collections", label: "Chosen collections" },
  { key: "works", label: "Chosen works" },
] as const;

export type SourceKind = (typeof SOURCE_KINDS)[number]["key"];

/**
 * The copy, in one place.
 *
 * A viewing room is the first thing in ONDO that shows a work to somebody with
 * no account, so most of this is spent saying precisely what it does and does
 * not disclose. That is not caution for its own sake: a collector who believes a
 * room shows less than it does will put the wrong work in one.
 */
export const VIEWING_ROOMS_COPY = {
  loading: "Loading your viewing rooms…",
  unknownError: "Something went wrong. Please try again.",

  emptyLead: "No viewing rooms yet.",
  emptyDetail:
    "A viewing room is a page you can send to someone. They see the works you put in it, presented as the registry holds them, and nothing else about your collection — not where a work is, not who holds it, not which collection it came from.",

  handleLead: "Choose the address your rooms live at.",
  handleDetail:
    "Every room you open is a page under this name. Letters, numbers and hyphens. It is set once and cannot be changed afterwards, because changing it would break every link you had already sent — so pick something you would be happy to put in front of a client.",
  handleLabel: "Your address",
  handlePlaceholder: "hue-williams",
  handleAction: "Take this address",
  handleBusy: "Claiming…",
  handleInvalid:
    "Use 3 to 30 letters, numbers or hyphens — no spaces, and not starting or ending with a hyphen.",
  /** The server answers malformed, reserved and taken with one message. So do we. */
  handleUnavailable: "That address is not available. Try another.",

  createAction: "New viewing room",
  createHeading: "A new viewing room",

  edit: "Edit",
  editHeading: "Edit this room",
  editSubmit: "Save changes",
  editBusy: "Saving…",
  editDone: "Changes saved.",
  /**
   * Said at the top of the edit form and nowhere else.
   *
   * The link does not change and the room does not close while it is being
   * edited, so a change is live the moment it is saved — and anybody who has
   * already been in has already seen what was there. That is not a warning
   * against editing; it is the one thing about it that is not obvious.
   */
  editLiveNote:
    "This room is already open. The link stays the same, and changes take effect for anyone holding it as soon as you save — but whoever has already been in has already seen what was there.",
  editPasswordLabel: "Password",
  editPasswordHint:
    "Leave this empty to keep the current password. Type a new one to change it. We cannot show you the old one.",
  editPasswordClear: "Remove the password, so anyone with the link can go in",
  titleLabel: "Name",
  titlePlaceholder: "Post-War British, for Sarah",
  titleRequired: "Give the room a name. Whoever opens the link will see it.",
  subtitleLabel: "Subtitle (optional)",
  subtitlePlaceholder: "Selected works, August 2026",
  messageLabel: "A note for whoever opens it (optional)",

  sourceLegend: "What is in the room",
  sourceEmpty: "Pick at least one, or put everything in the room.",
  tooMany: `A room can hold ${String(MAX_CHOSEN)} works. Remove some, or build the room over a whole collection instead.`,
  sourceNote:
    "Only works in collections you own can go in a room. A collection somebody shared with you is theirs to show.",
  sourceLoading: "Loading what you can publish…",
  sourceNothing:
    "You do not own a collection yet, so there is nothing to publish. Create one from Collections first.",
  sourceLiveNote:
    "A room over collections stays live: a work you register into one tomorrow appears in the room tomorrow. A room over chosen works is the list you picked and stays that way.",

  showsLegend: "What the room says about each work",
  showProvenance: "Show the chain of title",
  showProvenanceHint:
    "Every link in the work's history as the registry holds it. Past owners appear as they were recorded.",
  showPresentOwner: "…including the present owner",
  showPresentOwnerHint:
    "The last link in a chain is whoever holds the work now. Off, and the history a visitor sees stops short of today.",
  showDimensions: "Show dimensions and edition",
  showDimensionsHint:
    "Off, and the room shows the artist, the year and the medium — the catalogue entry without the object's measurements.",

  withheldTitle: "Never shown, whatever you choose",
  withheldBody:
    "Where the work is, who holds it, and which of your collections it sits in. A viewing room presents works; it does not disclose your holdings.",

  passwordLabel: "Password (optional)",
  passwordHint:
    "Set one and the room shows nothing at all until it is typed — not the works, not their titles, not how many there are. We cannot tell you what it was later; change it here if it is forgotten.",
  passwordShort: `A password needs at least ${PASSWORD_MIN} characters, or leave it empty.`,
  passwordSet: "Password set",
  passwordNone: "No password",
  passwordChange: "Change password",
  passwordRemove: "Remove password",
  passwordSave: "Save password",

  emailNote:
    "Whoever opens the room types an email address before going in, and it is recorded so you know who has looked. Nothing checks it and nothing is sent to it. A visitor signed in to ONDO is recorded by name instead, and is told so before they go in.",

  createSubmit: "Open the room",
  createBusy: "Opening…",
  cancel: "Cancel",

  createdLead: "The room is open.",
  createdNote:
    "Anyone with this link can see it — there is nothing else to let them in. Hide the room to close it again without losing it.",
  createdDone: "Back to my rooms",

  copyLink: "Copy link",
  copied: "Link copied",
  copyFailed: "Your browser would not let us copy. Select the address and copy it by hand.",

  hide: "Hide",
  reopen: "Reopen",
  hiddenBadge: "Hidden",
  hiddenNote: "Hidden. The link answers as though the room had never been there.",
  lockedBadge: "Password",

  remove: "Delete",
  removeConfirm: "Delete this room?",
  /**
   * Says the visit log SURVIVES, because it does.
   *
   * This sentence claimed the opposite until it was read against the database:
   * `set_viewing_room_status` retires a room rather than deleting the row,
   * precisely so `ondo.viewing_room_views` outlives it. Telling a collector
   * their record of who they had shown works to was being erased — when it was
   * being kept — is the worst direction for this copy to be wrong in.
   */
  removeConfirmDetail:
    "The link stops working straight away and cannot be brought back — the address is never given to another room. The record of who has already been in is kept.",
  removeConfirmAction: "Yes, delete it",
  keep: "Keep it",

  open: "Open",
  back: "← Back to my rooms",

  previewTitle: "What is in it",
  previewLoading: "Loading what is in this room…",
  previewEmpty:
    "Nothing is in this room. The works it named are no longer in a collection you own.",
  /** Said under a preview of a live room, because the two can differ. */
  previewLiveNote:
    "What the room publishes now. A work that leaves the collection it was in drops out of here on its own.",

  visitsTitle: "Who has been in",
  visitsLoading: "Loading who has been in…",
  visitsEmpty: "Nobody has opened this room yet.",
  visitsNote:
    "An address typed by a visitor is not verified — it is what they said, not what we checked. A visitor signed in to ONDO is named as the registry knows them.",
  visitsUnverified: "unverified",
  visitsMember: "ONDO account",
} as const;

